import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  ContentCopy,
  Check as CheckIcon,
  Code as JsonIcon,
  DeleteOutline as DeleteIcon,
  OpenInNew,
  Storage as PostgresIcon,
  Inbox as SqsIcon,
  ViewList as DdbIcon,
  Folder as S3Icon,
  Psychology as LlmIcon,
  Extension as GenericIcon,
  Apps as AppsIcon,
} from "@mui/icons-material";

import withErrorHandling from "../HOC/ErrorHandling";
import {
  getServiceCatalogByPath,
  getInstanceBindings,
  onDeleteCatalogByPath,
} from "../helpers/utils";
import Modal from "../components/Modal/Modal";
import Loader from "../components/Loading";
import t from "../helpers/en";
import { DELETE_ITEM, JSON_ITEM } from "../helpers/constants";

const KIND_META = {
  postgres: { label: "Postgres", Icon: PostgresIcon, tint: "#38bdf8" },
  awssqs: { label: "SQS", Icon: SqsIcon, tint: "#f472b6" },
  awsdynamodb: { label: "DynamoDB", Icon: DdbIcon, tint: "#a78bfa" },
  awss3bucket: { label: "S3 Bucket", Icon: S3Icon, tint: "#fbbf24" },
  "llm-gateway": { label: "LLM Gateway", Icon: LlmIcon, tint: "#34d399" },
};
const kindMeta = (p) =>
  KIND_META[p] || { label: p || "Service", Icon: GenericIcon, tint: "#94a3b8" };

const readStatus = (record, path) => {
  const s = record?.status || {};
  if (path === "postgres") return s.state;
  if (path === "awsdynamodb") return s.tableStatus;
  if (Array.isArray(s.conditions) && s.conditions.length > 0) {
    return s.conditions[0]?.status;
  }
  return "unknown";
};

/**
 * Per-instance detail page for Daari catalog services.
 * Route: /daari/:path/:name (e.g. /daari/postgres/db-1732306545532-mypg)
 *
 * Six sections, in render order:
 *   1. Hero — kind chip, displayName, k8sName in mono, status pill,
 *      right-aligned actions (Refresh, Copy binding secret name, Delete).
 *   2. Metadata — CRD-level context (kind, targetName, subtype, created).
 *   3. Connection details — kind-shaped keys pulled from the record's
 *      spec; secret values are NEVER echoed here (server-side contract).
 *   4. Bound apps — full list from GET /instances/{path}/{name}/bindings.
 *   5. Raw JSON — full record inside a collapsible panel.
 *   6. Danger zone — Delete, explicit confirm.
 */
const DaariInstanceDetail = ({ showError, showSuccess }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { path, name } = useParams();
  const navigate = useNavigate();

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bindings, setBindings] = useState([]);
  const [bindingsLoading, setBindingsLoading] = useState(false);
  const [modal, setModal] = useState({ open: false });
  const [copiedBinding, setCopiedBinding] = useState(false);

  const meta = kindMeta(path);
  const HeroIcon = meta.Icon;

  const refresh = useCallback(() => {
    if (!path || !name) return;
    setLoading(true);
    getServiceCatalogByPath({ path }, (result, err) => {
      setLoading(false);
      if (err) {
        showError?.(err);
        return;
      }
      const list = Array.isArray(result) ? result : [];
      const match = list.find((r) => r?.metadata?.name === name);
      setRecord(match || null);
    });
    setBindingsLoading(true);
    getInstanceBindings({ path, name }, (result, err) => {
      setBindingsLoading(false);
      if (err) {
        // Non-fatal — just leave bindings empty.
        setBindings([]);
        return;
      }
      setBindings(Array.isArray(result) ? result : []);
    });
  }, [path, name, showError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const displayName = useMemo(
    () =>
      record?.metadata?.labels?.["serviceCatalog.bindingDisplayName"] ||
      record?.metadata?.labels?.["serviceCatalog.targetDisplayName"] ||
      record?.metadata?.displayName ||
      record?.metadata?.name ||
      name,
    [record, name]
  );

  const bindingName =
    record?.metadata?.labels?.["serviceCatalog.bindingName"] ||
    (record?.metadata?.name ? `${record.metadata.name}-binding` : null);

  const statusText = readStatus(record, path);
  const status = String(statusText ?? "unknown").toLowerCase();

  const created = record?.metadata?.creationTimestamp
    ? new Date(record.metadata.creationTimestamp).toLocaleString("en-US")
    : "—";

  const details = useMemo(() => {
    const spec = record?.spec || {};
    if (path === "postgres") {
      const cls = spec?.dbInstanceClass?.replace("db.t4g.", "") || spec?.instanceSize;
      return [
        { key: "Instance class", value: cls || "—" },
        { key: "Allocated storage", value: spec?.allocatedStorage || spec?.diskSize || "—" },
        { key: "DB name", value: spec?.dbName || spec?.postgresDbName || "—" },
        { key: "Publicly accessible", value: String(spec?.publiclyAccessible ?? "—") },
      ];
    }
    if (path === "awss3bucket") {
      return [
        { key: "Region", value: record?.status?.location || spec?.region || "—" },
        { key: "Bucket ARN", value: record?.status?.arn || "—" },
      ];
    }
    if (path === "awsdynamodb") {
      return [
        { key: "Region", value: spec?.region || "—" },
        { key: "Table status", value: record?.status?.tableStatus || "—" },
      ];
    }
    if (path === "awssqs") {
      return [
        { key: "Queue URL", value: record?.status?.queueURL || "—" },
      ];
    }
    if (path === "llm-gateway") {
      return [
        { key: "Base URL", value: spec?.baseUrl || record?.status?.baseUrl || "—" },
      ];
    }
    return [];
  }, [path, record]);

  const provisioningIncomplete = useMemo(() => {
    // Postgres binding is created with host="" until the KRO controller
    // backfills. Detect and surface as a soft warning.
    if (path !== "postgres") return false;
    const hostEmpty =
      status === "creating" ||
      status === "pending" ||
      status === "provisioning";
    return hostEmpty;
  }, [path, status]);

  const openJson = () => setModal({ open: true, targetId: JSON_ITEM, json: record });
  const openDelete = () =>
    setModal({ open: true, targetId: DELETE_ITEM, ...record, path });

  const onCloseModal = () => setModal({ open: false });

  const onConfirmDelete = () => {
    if (modal?.isSubmitting) return;
    setModal((prev) => ({ ...prev, isSubmitting: true }));
    onDeleteCatalogByPath(
      { path, name: modal?.metadata?.name || name },
      (result, err) => {
        if (err) {
          setModal((prev) => ({ ...prev, isSubmitting: false }));
          showError?.(err);
          return;
        }
        onCloseModal();
        showSuccess?.(`Deleted '${displayName}' — cleanup in progress…`);
        navigate("/daari");
      }
    );
  };

  const handleCopyBinding = () => {
    if (!bindingName) return;
    navigator.clipboard?.writeText(bindingName);
    setCopiedBinding(true);
    setTimeout(() => setCopiedBinding(false), 1600);
  };

  const openApp = (appName) => {
    if (!appName) return;
    navigate(`/manage-apps?openApp=${encodeURIComponent(appName)}`);
  };

  if (loading && !record) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
        <Loader />
      </Box>
    );
  }

  if (!record) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/daari" underline="hover">
            Daari
          </Link>
          <Typography color="text.primary">{name}</Typography>
        </Breadcrumbs>
        <Alert severity="warning">
          Instance <b>{name}</b> not found under <b>{path}</b>. It may have
          been deleted.
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/daari")}
          sx={{ mt: 2, textTransform: "none" }}
        >
          Back to Daari
        </Button>
      </Box>
    );
  }

  let title, content, saveLabel, onConfirmModalAction;
  switch (modal?.targetId) {
    case JSON_ITEM:
      title = t.json;
      content = (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            fontSize: "0.8rem",
            fontFamily: "monospace",
            background: isDarkMode ? "#0f172a" : "#f8fafc",
            color: isDarkMode ? "#e2e8f0" : "#0f172a",
            borderRadius: 1,
            maxHeight: 550,
            overflow: "auto",
          }}
        >
          {JSON.stringify(modal.json, null, 2)}
        </Box>
      );
      break;
    case DELETE_ITEM:
      title = t.delete(`${path}: '${displayName}'`);
      onConfirmModalAction = onConfirmDelete;
      saveLabel = t.confirm;
      content = (
        <Box>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Delete this <b>{meta.label}</b> instance? The underlying resource
            will be de-provisioned.
          </Typography>
          {bindings.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              <b>{bindings.length}</b> app{bindings.length === 1 ? "" : "s"}{" "}
              currently bind to this service —{" "}
              {bindings.map((b, i) => (
                <span key={b?.name || i}>
                  {i > 0 && ", "}
                  <b>{b?.name}</b>
                </span>
              ))}
              . They will still start but their <code>C2A_SYSTEM_ENV</code>{" "}
              binding for this service will read empty until you rebind them
              to a replacement.
            </Alert>
          )}
        </Box>
      );
      break;
    default:
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Modal
        open={modal?.open}
        handleClose={onCloseModal}
        closeLabel={t.cancel}
        onSave={onConfirmModalAction}
        specificDetails={{ isSubmitting: modal?.isSubmitting }}
        {...{ saveLabel, title, content }}
      />

      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/daari" underline="hover">
          Daari
        </Link>
        <Typography color="text.primary">{displayName}</Typography>
      </Breadcrumbs>

      {/* Hero */}
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 2,
          borderColor: isDarkMode ? theme.palette.grey[800] : "#e2e8f0",
          background: isDarkMode
            ? "linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))"
            : "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `${meta.tint}22`,
              border: `1px solid ${meta.tint}55`,
            }}
          >
            <HeroIcon sx={{ color: meta.tint }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Chip
                size="small"
                label={meta.label}
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  border: `1px solid ${meta.tint}55`,
                  background: `${meta.tint}18`,
                  color: isDarkMode ? "#e2e8f0" : "#334155",
                }}
              />
              <Chip
                size="small"
                label={status}
                sx={{
                  height: 22,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  textTransform: "capitalize",
                  background:
                    status === "active" ||
                    status === "ready" ||
                    status === "true"
                      ? "rgba(34,197,94,0.15)"
                      : status === "creating" || status === "pending"
                      ? "rgba(245,158,11,0.15)"
                      : "rgba(148,163,184,0.15)",
                  color:
                    status === "active" || status === "ready" || status === "true"
                      ? "#16a34a"
                      : status === "creating" || status === "pending"
                      ? "#b45309"
                      : "#475569",
                }}
              />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: isDarkMode ? "#f1f5f9" : "#0f172a",
                wordBreak: "break-word",
              }}
            >
              {displayName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.25,
                color: isDarkMode ? "#94a3b8" : "#64748b",
                fontFamily: "monospace",
                fontSize: "0.75rem",
              }}
            >
              {name}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Refresh" arrow>
              <span>
                <IconButton onClick={refresh} disabled={loading}>
                  <RefreshIcon
                    sx={{
                      animation: loading ? "spin 1s linear infinite" : "none",
                      "@keyframes spin": {
                        from: { transform: "rotate(0deg)" },
                        to: { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            {bindingName && (
              <Tooltip title={copiedBinding ? "Copied!" : "Copy binding secret name"} arrow>
                <IconButton onClick={handleCopyBinding}>
                  {copiedBinding ? (
                    <CheckIcon color="success" />
                  ) : (
                    <ContentCopy />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="View JSON" arrow>
              <IconButton onClick={openJson}>
                <JsonIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete instance" arrow>
              <IconButton onClick={openDelete} color="error">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {provisioningIncomplete && (
          <Alert severity="info" sx={{ mt: 2, fontSize: "0.85rem" }}>
            Still provisioning. The KRO controller has not written the
            connection details back yet — bind will succeed, but bound apps
            may read empty values from <code>C2A_SYSTEM_ENV</code> until the
            state flips to <b>Active</b>.
          </Alert>
        )}
      </Paper>

      {/* Metadata + Connection details grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          mb: 2,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            borderColor: isDarkMode ? theme.palette.grey[800] : "#e2e8f0",
            background: isDarkMode ? theme.palette.grey[900] : "#fff",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            Metadata
          </Typography>
          <MetaRow label="Kind" value={meta.label} isDarkMode={isDarkMode} />
          <MetaRow label="k8s name" value={name} mono isDarkMode={isDarkMode} />
          <MetaRow
            label="Binding secret"
            value={bindingName || "—"}
            mono
            isDarkMode={isDarkMode}
          />
          <MetaRow label="Created" value={created} isDarkMode={isDarkMode} />
          <MetaRow
            label="Namespace"
            value={record?.metadata?.namespace || "—"}
            mono
            isDarkMode={isDarkMode}
          />
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            borderColor: isDarkMode ? theme.palette.grey[800] : "#e2e8f0",
            background: isDarkMode ? theme.palette.grey[900] : "#fff",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            Connection details
          </Typography>
          {details.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No connection details for this kind.
            </Typography>
          ) : (
            details.map((row) => (
              <MetaRow
                key={row.key}
                label={row.key}
                value={row.value}
                isDarkMode={isDarkMode}
              />
            ))
          )}
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 1.5,
              color: isDarkMode ? "#64748b" : "#94a3b8",
              fontSize: "0.7rem",
            }}
          >
            Secret values (username, password, tokens) never leave the cluster
            — bound apps read them at runtime via <code>C2A_SYSTEM_ENV</code>.
          </Typography>
        </Paper>
      </Box>

      {/* Bound apps */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          borderColor: isDarkMode ? theme.palette.grey[800] : "#e2e8f0",
          background: isDarkMode ? theme.palette.grey[900] : "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <AppsIcon sx={{ color: isDarkMode ? "#94a3b8" : "#64748b" }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Bound apps
          </Typography>
          <Chip
            size="small"
            label={bindings.length}
            sx={{
              height: 20,
              fontSize: "0.65rem",
              fontWeight: 700,
              background: isDarkMode
                ? "rgba(148,163,184,0.10)"
                : "rgba(15,23,42,0.05)",
            }}
          />
          <Box sx={{ flex: 1 }} />
          {bindingsLoading && <CircularProgress size={16} />}
        </Box>
        {bindings.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No apps bound yet. From an app's page, click{" "}
            <b>Bind a Service</b> and pick this instance.
          </Typography>
        ) : (
          <List disablePadding dense>
            {bindings.map((b, idx) => (
              <ListItem
                key={b?.name || idx}
                sx={{
                  py: 0.75,
                  px: 0,
                  borderTop:
                    idx === 0
                      ? "none"
                      : `1px solid ${
                          isDarkMode ? theme.palette.grey[800] : "#f1f5f9"
                        }`,
                }}
                secondaryAction={
                  <Tooltip title="Open on Apps" arrow>
                    <IconButton size="small" onClick={() => openApp(b?.name)}>
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <AppsIcon
                    fontSize="small"
                    sx={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: isDarkMode ? "#e2e8f0" : "#0f172a",
                      }}
                    >
                      {b?.name}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/daari")}
          sx={{ textTransform: "none" }}
        >
          Back to Daari
        </Button>
      </Box>

    </Box>
  );
};

const MetaRow = ({ label, value, mono, isDarkMode }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "140px 1fr",
      gap: 1,
      py: 0.5,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: isDarkMode ? "#94a3b8" : "#64748b",
        fontSize: "0.75rem",
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: isDarkMode ? "#e2e8f0" : "#0f172a",
        fontFamily: mono ? "monospace" : "inherit",
        fontSize: "0.75rem",
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default withErrorHandling(DaariInstanceDetail);
